import { API } from "@swivl-mono/api-helper";
import { RandomId, warn } from "@swivl-mono/helpers";
import {
  BrainInterface,
  BrainType,
  InheritanceStatus,
  LLMBrainInterface,
  LLMData,
  LLMModel,
  LocationVarKey,
  NLPBrainData,
  NLPBrainInterface,
  NLPBrainModel,
  Slug
} from "@swivl-mono/types";
import { create } from "zustand";

import { Member } from "../Member/Member.Current";
import LocationVarsModel from "../Variables/LocationVars.Model";

interface StoreInterface {
  brains?: BrainInterface[];
  brainsMap?: { [slug: Slug]: BrainInterface };
}

const BlankState: StoreInterface = {
  brains: undefined
};

export class BrainsModel {
  static useState = create<StoreInterface>((set) => ({ ...BlankState }));
  static setState = BrainsModel.useState.setState;
  static get state() {
    return BrainsModel.useState.getState();
  }
  static reset() {
    BrainsModel.setState({ ...BlankState });
  }

  static load(forceRefresh?: boolean) {
    if (BrainsModel.state.brains && BrainsModel.state.brains.length > 0 && !forceRefresh) {
      return;
    }
    API.Brains.getBrains(Member.orgId!)
      .then((brainsMap) => {
        BrainsModel.setState({ brains: Object.values(brainsMap), brainsMap: brainsMap });
      })
      .catch((err) => {
        warn("Error loading quests", err);
        return false;
      });
  }

  static loadBrain(brainSlug: Slug) {
    if (!brainSlug) {
      return;
    }
    API.Brains.getBrainBySlug(Member.orgId!, brainSlug)
      .then((data) => {
        let brainsMap = BrainsModel.state.brainsMap || {};
        brainsMap[brainSlug] = data;
        BrainsModel.setState({ brains: Object.values(brainsMap), brainsMap: brainsMap });
      })
      .catch((err) => {
        warn("Error loading quests", err);
        return false;
      });
  }

  static async createNLPBrain(name: string, data: NLPBrainData, model: NLPBrainModel): Promise<NLPBrainInterface> {
    const brain: Partial<NLPBrainInterface> = {
      slug: RandomId(10),
      name: name,
      orgId: Member.orgId!,
      type: BrainType.NLP,
      data: data,
      model: model,
      inheritance: InheritanceStatus.NONE
    };

    const newBrain = await API.Brains.createBrain(Member.orgId!, brain);

    let brainsMap = BrainsModel.state.brainsMap || {};
    brainsMap[newBrain.slug] = newBrain;
    BrainsModel.setState({ brains: Object.values(brainsMap), brainsMap: brainsMap });
    return newBrain as NLPBrainInterface;
  }
  static async createLLMBrain(name: string, data: LLMData, model: LLMModel): Promise<LLMBrainInterface> {
    const brain: Partial<LLMBrainInterface> = {
      slug: RandomId(10),
      name: name,
      orgId: Member.orgId!,
      type: BrainType.LLM,
      data: data,
      model: model,
      inheritance: InheritanceStatus.NONE
    };

    const newBrain = await API.Brains.createBrain(Member.orgId!, brain);
    let brainsMap = BrainsModel.state.brainsMap || {};
    brainsMap[newBrain.slug] = newBrain;
    BrainsModel.setState({ brains: Object.values(brainsMap), brainsMap: brainsMap });
    return newBrain as LLMBrainInterface;
  }

  static deleteBrain(brain: BrainInterface) {
    // throw new Error("Not Implemented")

    if (!brain) {
      warn("No Brain to delete");
      return Promise.resolve(true);
    }
    var brains = BrainsModel.state.brains;
    if (brains) {
      for (var i = 0; i < brains.length; i++) {
        if (brains[i].slug === brain.slug) {
          brains.splice(i, 1);
        }
      }
      BrainsModel.setState({ brains: brains });
    }
    API.Brains.deleteBrain(Member.orgId!, brain.id);
  }

  static updateBrain(brain: BrainInterface, shouldSave: boolean) {
    let brainsMap = BrainsModel.state.brainsMap || {};
    brainsMap[brain.slug] = brain;

    BrainsModel.setState({ brains: Object.values(brainsMap), brainsMap: brainsMap });
    if (shouldSave) {
      API.Brains.updateBrain(Member.orgId!, brain);
    }
  }

  static nameForBrain(brainSlug?: Slug) {
    if (!brainSlug) {
      return "Not Set";
    }
    if (!BrainsModel.state.brainsMap) {
      return undefined;
    }
    let brain = BrainsModel.state.brainsMap[brainSlug];
    if (!brain) {
      return "Unknown";
    }
    return brain.name;

    // if (brain.get("isGlobalBrain")) {
    //   return "Global Brain"
    // } else if (brain.get("quest")) {
    //   return brain.get("quest").get("name")
    // } else if (brain.get("journey")) {
    //   return brain.get("journey").get("name")
    // } else {
    //   return "Unknown Brain"
    // }
  }

  static getGlobalBrainSlug() {
    return LocationVarsModel.state.orgVariables[LocationVarKey.BrainSlug]?.value;
  }
  static async setGlobalBrainSlug(slug: Slug) {
    let id = LocationVarsModel.state.orgVariables[LocationVarKey.BrainSlug]?.id;
    if (!id) {
      const variable = await API.LocationVars.createVariable(LocationVarKey.BrainSlug, slug, Member.orgId!);
      if (!variable) {
        warn("No Brain Slug Variable Found ");
        return;
      }
      id = variable.id;
    }
    LocationVarsModel.saveVariable(id, slug);
  }
}
